import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { PromoCard } from '~/domains/design-system/components/PromoCard/PromoCard';
import type {
  PublicBoostTerms,
  VoucherTerms,
  BonusTermsCombined,
  PromoCardProps,
} from '~/domains/promotions/types';

import { PromoTermsContent } from './PromoTermsContent';
import { useStyles } from './PromoTermsModal.styles';

/**
 * Pure display contract for the promotion-terms modal.
 *
 * No data ops, no context reads, no hooks beyond `useStyles` /
 * `useTranslation`. Storybook can drive every variant directly with
 * literal props. The connected wrapper at `ConnectedPromoTermsModal`
 * is responsible for mapping a `boost | voucher | bonus` payload into
 * this shape.
 */
export type PromoTermsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  terms?: PublicBoostTerms | VoucherTerms | BonusTermsCombined;
  onAction?: () => void;
  promoCardProps: PromoCardProps;
};

function PromoTermsModal({
  isOpen,
  onClose,
  terms,
  onAction,
  promoCardProps,
}: PromoTermsModalProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('promotions');

  const handleAction = () => {
    onAction?.();
    onClose();
  };

  return (
    <Dialog
      type={{ mobile: 'drawer', desktop: 'modal' }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogBody className={classes.dialogBody}>
        <div className={classes.header}>
          <Text variant="headline-large" className={classes.headerTitle}>
            {t('modal.promoTerm.title')}
          </Text>

          <div className={classes.promoCardContainer}>
            <PromoCard
              {...promoCardProps}
              onAction={handleAction}
              variant="fat"
              alwaysShowExpiration
            />
          </div>
        </div>

        <div className={classes.content}>
          <PromoTermsContent terms={terms} />
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default PromoTermsModal;
